import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, CheckCircle, TrendingUp, Users, Zap, Building2, Globe, BarChart3, Phone, Mail, Clock, Target, Briefcase, Award } from 'lucide-react';
import { Header } from '@/layouts/Header';
import { Footer } from '@/layouts/Footer';
import { StickyCTA } from '@/components/StickyCTA';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from "@/features/shared/SEOHead";
import { trackCTAClick, trackNavClick } from '@/lib/utils';
import { generateLocalBusinessSchema } from '@/lib/seo-schema';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Sudbury = () => {
  // Set meta tags

  // Add JSON-LD structured data
  // Add JSON-LD structured data
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Do you serve Sudbury and Northern Ontario?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! Frayze is based in Thunder Bay, just 700km west of Sudbury, and we actively serve businesses across Northern Ontario. Our proximity means we understand Northern Ontario business challenges.distance, regional competition, and the need for affordable solutions."
          }
        },
        {
          "@type": "Question",
          "name": "Why should Sudbury businesses work with Thunder Bay-based Frayze?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Toronto and Ottawa agencies charge premium rates to cover big-city overhead. Frayze delivers the same quality services (CRM implementation, PPC management, web design, and marketing automation) without that overhead while keeping the expertise and results."
          }
        },
        {
          "@type": "Question",
          "name": "How much do your digital marketing services cost for Sudbury businesses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The 7-Day Website Launch is the entry point: a $997 build plus $189/month base, with communication usage billed like a phone plan. Larger managed growth systems (CRM, advertising, web design, and automation) are scoped to your business on a quick call, with a fixed quote before you commit. No hidden fees and no surprise invoices."
          }
        },
        {
          "@type": "Question",
          "name": "What makes you different from other digital marketing agencies?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most agencies offer siloed services. Frayze builds complete growth systems where your CRM, website, advertising, and automation all work together. We are automation specialists, and we track real metrics with transparent reporting."
          }
        },
        {
          "@type": "Question",
          "name": "Can you help mining and resource companies in Sudbury?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. We build integrated systems that handle complex multi-stakeholder projects, long sales cycles, and equipment tracking for mining supply companies, equipment dealers, and resource service providers."
          }
        },
        {
          "@type": "Question",
          "name": "How quickly can you get a Sudbury business set up and running?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Quick-Start Tools take 2-3 days. Custom website builds take 2-4 weeks. Complete CRM setup and automation takes 3-6 weeks, and full growth systems take 6-8 weeks."
          }
        },
        {
          "@type": "Question",
          "name": "Can you help with Google Ads and PPC campaigns targeting Sudbury customers?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. We specialize in local Google Ads campaigns that target Sudbury and Northern Ontario specifically, integrating all ad platforms with your CRM for clear attribution."
          }
        }
      ]
    };

    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Frayze Technologies Inc.",
      "description": "Digital marketing agency serving Sudbury and Northern Ontario with CRM implementation, PPC management, web design, SEO, and marketing automation services.",
      "url": "https://frayze.ca/service-areas/sudbury/",
      "telephone": "+18077000079",
      "email": "hello@frayze.ca",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Sudbury",
        "addressRegion": "ON",
        "addressCountry": "CA"
      },
      "areaServed": [
        { "@type": "City", "name": "Sudbury" },
        { "@type": "AdministrativeArea", "name": "Northern Ontario" }
      ],
      "serviceType": ["AI Automation", "CRM Systems", "Website Design", "Lead Generation", "SEO", "PPC Management"],
      "priceRange": "$$"
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://frayze.ca/" },
        { "@type": "ListItem", "position": 2, "name": "Service Areas", "item": "https://frayze.ca/service-areas/" },
        { "@type": "ListItem", "position": 3, "name": "Sudbury", "item": "https://frayze.ca/service-areas/sudbury/" }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify([faqSchema, localBusinessSchema, breadcrumbSchema]);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      title: "CRM Systems Sudbury",
      description: "Custom CRM implementations specifically for Sudbury's industries. Unify your data and automate client tracking from Valley East to Walden.",
      icon: Users,
      link: "/solutions/crm-automation/"
    },
    {
      title: "Lead Generation",
      description: "High-intent lead generation for Sudbury businesses. Local ads and AI-powered qualification to fill your sales pipeline.",
      icon: Target,
      link: "/solutions/lead-generation-system/"
    },
    {
      title: "Web Design & Development",
      description: "Premium, mobile-responsive websites designed for the Sudbury market. High-performance sites that rank and convert local visitors.",
      icon: Globe,
      link: "/solutions/websites/"
    },
    {
      title: "Business Automation",
      description: "Zapier, Make.com, and custom workflow automation for Sudbury operations. Eliminate manual tasks and scale efficiently.",
      icon: Zap,
      link: "/solutions/crm-automation/"
    },
    {
      title: "Local SEO Services",
      description: "Dominate search in Greater Sudbury. Google Business Profile optimization and local SEO to own your neighborhood's results.",
      icon: TrendingUp,
      link: "/solutions/local-seo-reputation/"
    },
    {
      title: "Custom AI Solutions",
      description: "AI-powered automation for Sudbury enterprises. Custom chatbots and voice AI systems to streamline Northern Ontario operations.",
      icon: Briefcase,
      link: "/solutions/ai-strategy-rollout/"
    }
  ];

  const sudburyInsights = [
    {
      stat: "165K+",
      label: "Greater Sudbury Population",
      description: "Northern Ontario's largest city"
    },
    {
      stat: "15,000+",
      label: "Small & Medium Businesses",
      description: "Diverse local economy"
    },
    {
      stat: "$8B+",
      label: "Annual GDP",
      description: "Mining, healthcare, and retail hub"
    },
    {
      stat: "3+",
      label: "Major Universities",
      description: "Laurentian, Cambrian College, Boreal"
    }
  ];

  const industries = [
    { name: "Mining & Resources", icon: Building2, href: "/industries/trades/" },
    { name: "Healthcare & Medical", icon: Users, href: "/solutions/" },
    { name: "Retail & Commerce", icon: Globe, href: "/case-studies/" },
    { name: "Education & Training", icon: Briefcase, href: "/solutions/" },
    { name: "Construction", icon: BarChart3, href: "/industries/trades/" },
    { name: "Professional Services", icon: Award, href: "/solutions/" }
  ];

  return (
    <div className="min-h-screen bg-background">

      <Header />
      <SEOHead
        title="Sudbury Lead Generation & Marketing Agency | SEO & CRM | Frayze"
        description="Frayze is the Sudbury lead generation & marketing agency that turns local search into booked revenue. SEO, web design, CRM automation & ads for Greater Sudbury businesses."
        keywords="sudbury lead generation, lead generation sudbury, marketing agency sudbury, digital marketing sudbury, seo sudbury, web design sudbury, crm implementation sudbury, ai automation sudbury"
        ogTitle="Sudbury Lead Generation & Marketing Agency | SEO & CRM | Frayze"
        ogDescription="Frayze is the Sudbury lead generation & marketing agency that turns local search into booked revenue. SEO, web design, CRM automation & ads for Greater Sudbury businesses."
        twitterTitle="Sudbury Marketing Agency | Digital Marketing, SEO & CRM | Frayze"
        twitterDescription="Frayze is the Sudbury marketing agency that turns local search into booked revenue. Digital marketing, SEO, web design, and CRM automation for Greater Sudbury businesses."
        canonical="https://frayze.ca/service-areas/sudbury/"
        noindex={false}
      />

      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-4 absolute top-20 z-20">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/service-areas/">Service Areas</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Sudbury</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-40 md:pt-48 pb-20">
          <div
            className="absolute top-0 left-0 w-full h-[800px] pointer-events-none overflow-hidden z-0"
            style={{
              maskImage: 'radial-gradient(ellipse 72% 58% at 50% 40%, black 0%, black 25%, transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse 72% 58% at 50% 40%, black 0%, black 25%, transparent 70%)'
            }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(0,102,255,0.35),transparent_55%),radial-gradient(circle_at_82%_12%,rgba(127,0,255,0.28),transparent_60%),radial-gradient(circle_at_50%_82%,rgba(0,204,255,0.18),transparent_65%)] blur-3xl" />
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-6 border-primary/20 bg-primary/5 text-primary backdrop-blur-sm">
                <MapPin className="h-3 w-3 mr-1.5" />
                Serving Sudbury & Northern Ontario
              </Badge>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
                Sudbury Lead Generation
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  Marketing, SEO & CRM: Built for Growth
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-muted-foreground mb-10 leading-relaxed max-w-3xl mx-auto">
                Get found by more nearby customers, capture more local leads, and follow up fast. We help Sudbury
                businesses grow with CRM systems, ads, web design, and automation, all managed remotely with local results.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-10">
                <Button
                  size="lg"
                  className="h-14 px-8 text-lg font-bold shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-300 group"
                  asChild
                >
                  <a
                    href="/book-consultation/"
                    onClick={() => {
                      trackCTAClick({
                        cta_text: 'Book Free Sudbury Consultation',
                        cta_location: 'sudbury_hero',
                        cta_type: 'consultation_cta',
                        cta_url: '/book-consultation/'
                      });
                    }}
                  >
                    Get More Local Leads
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="h-14 px-8 text-lg hover:bg-muted/50 bg-background/50 backdrop-blur-sm"
                  asChild
                >
                  <a
                    href="/case-studies/"
                    onClick={() => {
                      trackNavClick({
                        nav_text: 'View Our Work',
                        nav_location: 'sudbury_hero',
                        nav_type: 'case_studies_navigation',
                        nav_url: '/case-studies/'
                      });
                    }}
                  >
                    View Our Work
                  </a>
                </Button>
              </div>

              <div className="flex flex-wrap gap-6 justify-center text-sm font-medium text-muted-foreground">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-primary" />
                  <span>Serving Northern Ontario</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-primary" />
                  <span>Remote-First, Results-Driven</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-primary" />
                  <span>Zoom Meetings, Local Focus</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sudbury Market Insights */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Why Sudbury Businesses Choose Frayze
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Local owners pick us to get found by nearby customers, fill their pipeline with local leads, and reply
                faster than the competition. Here's why Sudbury businesses partner with Frayze to grow.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {sudburyInsights.map((insight, index) => (
                <Card key={index} className="text-center">
                  <CardContent className="pt-6">
                    <div className="text-4xl font-bold bg-gradient-cta bg-clip-text text-transparent mb-2">
                      {insight.stat}
                    </div>
                    <div className="font-semibold text-lg mb-2">{insight.label}</div>
                    <p className="text-sm text-muted-foreground">{insight.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-8 md:p-12 mt-12 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-6 text-center">The Sudbury Advantage: Northern Ontario Expertise</h3>
              <div className="grid md:grid-cols-2 gap-8 text-left">
                <div>
                  <h4 className="font-semibold mb-3 flex items-center text-primary">
                    <Building2 className="h-5 w-5 mr-2" />
                    Sudbury's Diverse Economy
                  </h4>
                  <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                    Sudbury is Northern Ontario's largest city with a diverse economy spanning mining, healthcare,
                    retail, education, and construction. Businesses here need practical, affordable digital solutions
                    that work in a regional market.
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    From mining supply companies to healthcare practices to local retailers, Sudbury businesses need
                    integrated systems that can compete with bigger southern Ontario competitors.without southern prices.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-3 flex items-center text-primary">
                    <MapPin className="h-5 w-5 mr-2" />
                    Why a Thunder Bay Agency?
                  </h4>
                  <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                    We understand Northern Ontario business. Sudbury businesses need affordable, effective digital
                    marketing without paying Toronto or Ottawa rates. Frayze delivers competitive services at regional
                    pricing while maintaining high standards.
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    As fellow Northern Ontarians, we get the challenges: distance, regional competition, tight budgets,
                    and the need for solutions that actually work. No corporate overhead means better value for Sudbury businesses.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services for Sudbury */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Digital Marketing Services for
                <span className="bg-gradient-cta bg-clip-text text-transparent ml-3">Sudbury Businesses</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Everything you need to get found, win more local customers, and follow up fast.from CRM and local SEO
                to Google Ads built for the Sudbury market.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardHeader>
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                      <CardDescription>{service.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button variant="link" className="p-0 h-auto text-primary" asChild>
                        <Link
                          to={service.link}
                          onClick={() => {
                            trackNavClick({
                              nav_text: `Learn More - ${service.title}`,
                              nav_location: 'sudbury_services',
                              nav_type: 'service_navigation',
                              nav_url: service.link
                            });
                          }}
                        >
                          Learn More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Sudbury Industries We Serve
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                From mining to healthcare to retail, we've helped Sudbury businesses across diverse sectors
                scale their digital operations and drive measurable growth.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto mb-12">
              {industries.map((industry, index) => {
                const IconComponent = industry.icon;
                return (
                  <Link key={index} to={industry.href} className="block">
                    <Card className="text-center hover:shadow-lg hover:border-primary/40 transition-all h-full">
                      <CardContent className="pt-6">
                        <IconComponent className="h-8 w-8 mx-auto mb-3 text-primary" />
                        <p className="text-sm font-medium">{industry.name}</p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl text-center">Sudbury-Specific Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center text-lg">
                      <CheckCircle className="h-5 w-5 mr-2 text-growth" />
                      Mining & Resource Industries
                    </h4>
                    <p className="text-muted-foreground mb-4">
                      Sudbury's mining sector needs sophisticated CRM and automation systems for B2B sales, equipment
                      supply chains, and contractor management. We build systems that handle complex supply chains,
                      multi-stakeholder projects, and long sales cycles typical in mining.
                    </p>
                    <p className="text-muted-foreground">
                      Our clients use integrated platforms that connect their operations to customer communications, from
                      initial inquiry through project completion, with one system replacing disconnected spreadsheets and manual tracking.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center text-lg">
                      <CheckCircle className="h-5 w-5 mr-2 text-growth" />
                      Healthcare & Medical Practices
                    </h4>
                    <p className="text-muted-foreground mb-4">
                      Sudbury's healthcare sector requires compliant, efficient systems for patient management, appointment
                      scheduling, and clinic operations. We implement systems that respect privacy while automating patient
                      communications and reducing administrative overhead.
                    </p>
                    <p className="text-muted-foreground">
                      Whether you're a medical practice, dental clinic, or therapy center, we deliver automation that improves
                      patient experience while reducing staff workload.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Related Service Areas */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Serving Businesses Across
                <span className="bg-gradient-cta bg-clip-text text-transparent ml-3">Canada</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Remote-first digital marketing services for businesses nationwide
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
              <Card className="hover:shadow-lg transition-all border-2 hover:border-primary">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2">Thunder Bay, Ontario</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Local digital solutions for Thunder Bay businesses
                  </p>
                  <Button variant="ghost" size="sm" className="p-0 h-auto text-primary" asChild>
                    <Link to="/service-areas/thunder-bay/">
                      Learn More
                      <ArrowRight className="h-4 w-4 ml-1 inline" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all border-2 hover:border-primary">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2">Toronto, Ontario</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Digital marketing services for GTA businesses
                  </p>
                  <Button variant="ghost" size="sm" className="p-0 h-auto text-primary" asChild>
                    <Link to="/service-areas/toronto/">
                      Learn More
                      <ArrowRight className="h-4 w-4 ml-1 inline" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>

        {/* Sudbury FAQ */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Frequently Asked Questions
                  <span className="block mt-2 text-xl text-muted-foreground font-normal">
                    About Working with Frayze for Sudbury Services
                  </span>
                </h2>
              </div>

              <div className="max-w-3xl mx-auto text-left">
                <Accordion type="single" collapsible className="w-full space-y-4">
                  <AccordionItem value="item-1" className="border border-primary/10 rounded-xl px-6 bg-background/50 backdrop-blur-sm">
                    <AccordionTrigger className="text-base font-semibold hover:no-underline hover:text-primary py-4">
                      Do you serve Sudbury and Northern Ontario?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                      Yes! Frayze is based in Thunder Bay, just 700km west of Sudbury, and we actively serve businesses across
                      Northern Ontario. Our proximity means we understand Northern Ontario business challenges.distance, regional
                      competition, and the need for affordable solutions. We conduct discovery sessions, strategy meetings, and
                      training via Zoom, and provide 24/7 access to project dashboards and analytics.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-2" className="border border-primary/10 rounded-xl px-6 bg-background/50 backdrop-blur-sm">
                    <AccordionTrigger className="text-base font-semibold hover:no-underline hover:text-primary py-4">
                      Why should Sudbury businesses work with Thunder Bay-based Frayze?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                      <p className="mb-3">
                        Toronto and Ottawa agencies charge premium rates to cover big-city overhead. Frayze delivers the same
                        quality services (CRM implementation, PPC management, web design, and marketing automation) without that
                        overhead, while keeping the expertise and results.
                      </p>
                      <p>
                        As Northern Ontarians, we understand your market: competing with bigger southern Ontario companies, tight
                        budgets, and the need for practical solutions that work. No corporate overhead means better value for Sudbury businesses.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3" className="border border-primary/10 rounded-xl px-6 bg-background/50 backdrop-blur-sm">
                    <AccordionTrigger className="text-base font-semibold hover:no-underline hover:text-primary py-4">
                      How much do your digital marketing services cost for Sudbury businesses?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                      <p className="mb-3">
                        <strong>7-Day Website Launch:</strong> the entry point, a $997 build plus $189/month base, with
                        communication usage billed like a phone plan. Live in seven days, no contracts, and you own the site.
                      </p>
                      <p>
                        <strong>Managed Growth Systems:</strong> CRM, advertising, web design, and automation builds are
                        scoped to your business on a quick call. You get a fixed quote before you commit: no hidden fees
                        and no surprise invoices.
                        <Link to="/case-studies/" className="text-primary hover:underline ml-1">View our case studies →</Link>
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4" className="border border-primary/10 rounded-xl px-6 bg-background/50 backdrop-blur-sm">
                    <AccordionTrigger className="text-base font-semibold hover:no-underline hover:text-primary py-4">
                      What makes you different from other digital marketing agencies?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                      <p className="mb-3">
                        <strong>Integrated Systems, Not Scattered Tools:</strong> Most agencies offer siloed services (just SEO,
                        just ads, just web design). Frayze builds complete growth systems where your CRM, website, advertising,
                        and automation all work together. One dashboard, one partner, one unified strategy.
                      </p>
                      <p className="mb-3">
                        <strong>AI-Powered Automation:</strong> We're automation specialists. Every system we build includes
                        intelligent workflows, AI-assisted tools, and data integrations that eliminate manual tasks and scale operations.
                      </p>
                      <p>
                        <strong>Transparent, Results-Driven:</strong> No fluff, no 12-month contracts. We track real metrics
                        (leads, conversions, revenue) and give full access to analytics dashboards. If we're not delivering results,
                        you're not locked in.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-5" className="border border-primary/10 rounded-xl px-6 bg-background/50 backdrop-blur-sm">
                    <AccordionTrigger className="text-base font-semibold hover:no-underline hover:text-primary py-4">
                      Can you help mining and resource companies in Sudbury?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                      <p className="mb-3">
                        Absolutely. Mining supply companies, equipment dealers, and resource service providers need sophisticated
                        CRM systems for B2B sales, contractor management, and supply chain coordination. We build integrated systems
                        that handle complex multi-stakeholder projects, long sales cycles, and equipment tracking.
                      </p>
                      <p>
                        Our CRM solutions for mining businesses include project management, equipment inventory, contractor databases,
                        and automated communications for leads from initial inquiry through project completion.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-6" className="border border-primary/10 rounded-xl px-6 bg-background/50 backdrop-blur-sm">
                    <AccordionTrigger className="text-base font-semibold hover:no-underline hover:text-primary py-4">
                      How quickly can you get a Sudbury business set up and running?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                      <p className="mb-2">
                        <strong>Quick-Start Tools:</strong> 2-3 days for productized solutions (AI Lead Fix, Review Boost)
                      </p>
                      <p className="mb-2">
                        <strong>Website Projects:</strong> 2-4 weeks for custom website builds with CRM integration
                      </p>
                      <p className="mb-2">
                        <strong>CRM Implementation:</strong> 3-6 weeks for complete <Link to="/solutions/crm-automation/" className="text-primary hover:underline">CRM setup and automation</Link>, and team training
                      </p>
                      <p>
                        <strong>Full Growth Systems:</strong> 6-8 weeks for comprehensive packages (CRM + website + ads + automation)
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-7" className="border border-primary/10 rounded-xl px-6 bg-background/50 backdrop-blur-sm">
                    <AccordionTrigger className="text-base font-semibold hover:no-underline hover:text-primary py-4">
                      Can you help with Google Ads and PPC campaigns targeting Sudbury customers?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                      <p className="mb-3">
                        Absolutely. We specialize in local Google Ads campaigns that target Sudbury and Northern Ontario specifically.
                        Whether you're a local retailer, healthcare practice, mining supply company, or professional service, we build
                        data-driven PPC campaigns with geographic precision.
                      </p>
                      <p>
                        Our PPC services include Google Ads (Search, Display, YouTube), Facebook/Instagram ads, and retargeting strategies.
                        We integrate all ad platforms with your CRM so every lead is tracked, with clear attribution back to ad spend.
                        <Link to="/solutions/lead-generation-system/" className="text-primary hover:underline ml-1">Learn more about PPC services →</Link>
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden py-24 bg-muted/30">
          <div
            className="absolute top-0 left-0 w-full h-[600px] pointer-events-none overflow-hidden z-0"
            style={{
              maskImage: 'radial-gradient(ellipse 72% 58% at 50% 40%, black 0%, black 25%, transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse 72% 58% at 50% 40%, black 0%, black 25%, transparent 70%)'
            }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,102,255,0.15),transparent_60%)] blur-3xl" />
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">
                Ready to Scale Your Sudbury Business?
              </h2>
              <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
                Book a free consultation to discuss your digital marketing goals. We'll audit your current systems,
                identify growth opportunities, and show you exactly how Frayze can help you dominate the Northern Ontario market.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card className="hover:shadow-lg transition-all duration-300 border-primary/10 bg-background/50 backdrop-blur-sm">
                  <CardContent className="pt-6 text-center">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Phone className="h-6 w-6 text-primary" />
                    </div>
                    <p className="font-semibold mb-1">Call or Text</p>
                    <a href="tel:+18077000079" className="text-sm text-primary hover:underline font-medium">+1 (807) 700-0079</a>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-all duration-300 border-primary/10 bg-background/50 backdrop-blur-sm">
                  <CardContent className="pt-6 text-center">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    <p className="font-semibold mb-1">Email Us</p>
                    <a href="mailto:hello@frayze.ca" className="text-sm text-primary hover:underline font-medium">hello@frayze.ca</a>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-all duration-300 border-primary/10 bg-background/50 backdrop-blur-sm">
                  <CardContent className="pt-6 text-center">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <p className="font-semibold mb-1">Response Time</p>
                    <p className="text-sm text-muted-foreground">Within 24 hours</p>
                  </CardContent>
                </Card>
              </div>

              <Button
                size="lg"
                className="h-14 px-10 text-lg font-bold shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-300 group"
                asChild
              >
                <a
                  href="/book-consultation/"
                  onClick={() => {
                    trackCTAClick({
                      cta_text: 'Book Your Free Sudbury Consultation',
                      cta_location: 'sudbury_final_cta',
                      cta_type: 'consultation_cta',
                      cta_url: '/book-consultation/'
                    });
                  }}
                >
                  Get My Free Sudbury Growth Plan
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>

              <p className="text-sm text-muted-foreground mt-8">
                Serving Sudbury, Greater Sudbury, and all of Northern Ontario
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyCTA />
    </div>
  );
};

export default Sudbury;

